import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Lightbulb } from 'lucide-react';
import { trackMiniAppEvent } from '../../utils/analytics';
import {
  getEngineRootStyle,
  titleStyle,
  instructionsStyle,
  primaryBtn,
  secondaryBtn,
  feedbackCorrect,
  feedbackIncorrect,
  feedbackWarmHint,
  ENGINE_AUTO_ADVANCE_MS,
} from './shared/engineTheme';
import { shouldSuppressActivityTitle } from './shared/guidedPromptUtils';
import { normalizePromptForDedupe } from './shared/guidedTapRoundUi';
import { useCelebration, CelebrationOverlay, A11yAnnouncement } from './shared/Celebration';
import { useGuidedHints } from './shared/useGuidedHints';
import GuidedHintOverlay, {
  GuidedHintHelpButton,
  guidedHintReadAloud,
} from './shared/GuidedHintOverlay';
import { isTapFirstLessonUi } from './shared/lessonUiMode';

/**
 * When prompts follow `Goal: … Plan: …` (optional trailing `Which…`), show Goal vs Plan on separate rows.
 * Tap-first `FlatMcPrompt` shows Goal + Plan + question; desktop uses {@link GoalPlanPromptBlocks}.
 * @returns {{ goal: string, plan: string, question: string | null } | null}
 */
function parseGoalPlanPrompt(prompt) {
  if (typeof prompt !== 'string' || !prompt.trim()) return null;
  const parts = prompt.split(/\s+Plan:\s*/i);
  if (parts.length !== 2) return null;
  const goal = parts[0].replace(/^\s*Goal:\s*/i, '').trim();
  if (!goal) return null;
  let rest = parts[1].trim();
  let question = null;
  const qDot = rest.match(/^([\s\S]+?)(\.\s+(Which\b[\s\S]+))$/i);
  if (qDot) {
    rest = qDot[1].trim();
    question = qDot[2].replace(/^\.\s*/, '').trim();
  } else {
    const qSpace = rest.match(/^([\s\S]+?)(\s+(Which\b[\s\S]+))$/i);
    if (qSpace && /\bWhich\b/i.test(qSpace[2])) {
      rest = qSpace[1].trim();
      question = qSpace[2].trim();
    }
  }
  return { goal, plan: rest, question };
}

/** Split "context — short question?" into two lines for tap-first layouts (no Goal:/Plan: schema). */
function splitEmDashPrompt(prompt) {
  if (typeof prompt !== 'string' || !prompt.trim()) return null;
  const chunks = prompt.split(/\s*[—–]\s+/);
  if (chunks.length < 2) return null;
  const subject = chunks[0].trim();
  const rest = chunks.slice(1).join(' — ').trim();
  if (subject.length < 8 || rest.length < 6) return null;
  return { subject, question: rest };
}

/** First visible question line for tap-first title vs prompt dedupe (mirrors FlatMcPrompt heuristics). */
function mcPromptPrimaryLineForTitleDedupe(prompt) {
  if (typeof prompt !== 'string') return '';
  const trimmed = prompt.trim();
  if (!trimmed) return '';
  const gpp = parseGoalPlanPrompt(trimmed);
  if (gpp) {
    const lead = gpp.question || gpp.plan;
    return (lead || '').split(/\n+/)[0].trim();
  }
  const em = splitEmDashPrompt(trimmed);
  if (em) return em.question.trim();
  return trimmed.split(/\n+/)[0].trim();
}

function FlatMcPrompt({ prompt, narrow }) {
  const parsed = parseGoalPlanPrompt(prompt);
  const mb = narrow ? 8 : 10;
  if (parsed) {
    const { goal } = parsed;
    const lead = parsed.question || parsed.plan;
    const sub = parsed.question ? parsed.plan : null;
    return (
      <div style={{ marginBottom: mb }}>
        {goal ? (
          <p
            style={{
              margin: '0 0 6px',
              fontSize: narrow ? 14 : 15,
              fontWeight: 600,
              lineHeight: 1.4,
              color: 'var(--color-text)',
            }}
          >
            <span
              style={{
                display: 'block',
                marginBottom: 4,
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'var(--color-text-soft, var(--color-text))',
                opacity: 0.82,
              }}
            >
              Goal
            </span>
            {/[.!?…]$/.test(goal) ? goal : `${goal}.`}
          </p>
        ) : null}
        {sub ? (
          <p
            style={{
              margin: '0 0 6px',
              fontSize: narrow ? 15 : 16,
              fontWeight: 650,
              lineHeight: 1.35,
              color: 'var(--color-text)',
            }}
          >
            <span
              style={{
                display: 'block',
                marginBottom: 4,
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'var(--color-text-soft, var(--color-text))',
                opacity: 0.82,
              }}
            >
              Plan
            </span>
            {/[.!?…]$/.test(sub) ? sub : `${sub}.`}
          </p>
        ) : null}
        <p
          style={{
            margin: 0,
            fontSize: narrow ? 16 : 17,
            fontWeight: 800,
            lineHeight: 1.35,
            color: 'var(--color-text)',
          }}
        >
          {lead}
        </p>
      </div>
    );
  }
  const dash = splitEmDashPrompt(prompt);
  if (dash) {
    const subj = dash.subject;
    const subjLine = /[.!?…]$/.test(subj) ? subj : `${subj}.`;
    const qLine = dash.question.endsWith('?') ? dash.question : `${dash.question}?`;
    return (
      <div style={{ marginBottom: mb }}>
        <p
          style={{
            margin: '0 0 6px',
            fontSize: narrow ? 15 : 16,
            fontWeight: 650,
            lineHeight: 1.35,
            color: 'var(--color-text)',
          }}
        >
          {subjLine}
        </p>
        <p
          style={{
            margin: 0,
            fontSize: narrow ? 16 : 17,
            fontWeight: 800,
            lineHeight: 1.35,
            color: 'var(--color-text)',
          }}
        >
          {qLine}
        </p>
      </div>
    );
  }
  return (
    <p
      style={{
        margin: `0 0 ${mb}px`,
        fontSize: narrow ? 16 : 17,
        fontWeight: 700,
        lineHeight: 1.35,
        color: 'var(--color-text)',
      }}
    >
      {prompt}
    </p>
  );
}

function useNarrowViewport(breakpointPx = 640) {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(`(max-width: ${breakpointPx}px)`).matches : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia(`(max-width: ${breakpointPx}px)`);
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [breakpointPx]);

  return narrow;
}

function GoalPlanPromptBlocks({ prompt }) {
  const parsed = parseGoalPlanPrompt(prompt);
  if (!parsed) {
    return <p style={{ ...instructionsStyle, marginBottom: 12 }}>{prompt}</p>;
  }

  const labelStyle = {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    marginBottom: 6,
    color: 'var(--color-primary)',
  };

  const goalBox = {
    padding: '10px 12px',
    borderRadius: 12,
    borderLeft: '4px solid var(--color-primary)',
    background: 'rgba(139, 92, 246, 0.07)',
    marginBottom: 10,
    borderTop: '1px solid var(--color-border)',
    borderRight: '1px solid var(--color-border)',
    borderBottom: '1px solid var(--color-border)',
  };

  const planBox = {
    padding: '10px 12px',
    borderRadius: 12,
    borderLeft: '4px solid rgba(56, 189, 248, 0.95)',
    background: 'rgba(56, 189, 248, 0.07)',
    marginBottom: parsed.question ? 12 : 12,
    borderTop: '1px solid var(--color-border)',
    borderRight: '1px solid var(--color-border)',
    borderBottom: '1px solid var(--color-border)',
  };

  const bodyStyle = {
    margin: 0,
    fontSize: 15,
    lineHeight: 1.45,
    fontWeight: 600,
    color: 'var(--color-text)',
  };

  const planBodyStyle = {
    ...bodyStyle,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSize: 14,
    fontWeight: 650,
    letterSpacing: '0.02em',
  };

  const questionStyle = {
    ...instructionsStyle,
    marginBottom: 0,
    fontWeight: 800,
    fontSize: 16,
    lineHeight: 1.35,
    paddingTop: 2,
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={goalBox}>
        <div style={labelStyle}>Goal</div>
        <p style={bodyStyle}>{parsed.goal}</p>
      </div>
      <div style={planBox}>
        <div style={{ ...labelStyle, color: 'rgba(56, 189, 248, 0.95)' }}>Plan</div>
        <p style={planBodyStyle}>{parsed.plan}</p>
      </div>
      {parsed.question ? <p style={questionStyle}>{parsed.question}</p> : null}
    </div>
  );
}

/**
 * Multiple-choice quiz from JSON `content`.
 *
 * @param {object} props.content - {
 *   instructions?,
 *   questions: [{
 *     id, prompt,
 *     choices: [{ id, text, correct, followUp? }],
 *     explanation?,
 *     followUp?: { prompt, hint?, choices: [{ id, text, correct, explanation? }] }
 *   }]
 * }
 *
 * Branching: a wrong answer on a question with `followUp` opens a guided
 * sub-question. After answering it, the learner can retry the main question
 * (it counts as correct-with-help in the final score).
 */
export default function MultipleChoiceEngine({
  title = 'Quiz',
  content = {},
  hints = [],
  explanation,
  activityId = 'engine_multiple_choice',
  onProgress,
  onComplete,
  guidedHintsEnabled = true,
  onRegisterLessonFooterActions,
  lessonUiMode = 'default',
  guidedContext,
}) {
  const questions = content.questions || [];
  const tapFirst = isTapFirstLessonUi(lessonUiMode);
  const singleSurface = guidedContext?.singleSurface === true;
  const stepTitle = typeof guidedContext?.stepTitle === 'string' ? guidedContext.stepTitle : '';
  const suppressActivityTitle = shouldSuppressActivityTitle({ stepTitle, activityTitle: title });
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState(null);
  const [phase, setPhase] = useState('pick');
  const [correctCount, setCorrectCount] = useState(0);
  const [helpedCount, setHelpedCount] = useState(0);
  const [helpedThisQuestion, setHelpedThisQuestion] = useState(false);
  const [followPicked, setFollowPicked] = useState(null);
  /** Lesson shell: after Check on a wrong main answer, show reveal styling while staying in `pick` (footer stays Check — same idea as MatchEngine tap rounds). */
  const [shellWrongReveal, setShellWrongReveal] = useState(null);
  const [showCelebration, celebrate] = useCelebration();
  const [a11yMsg, setA11yMsg] = useState('');
  const revealTimerRef = useRef(null);
  const quickCheckRef = useRef(null);
  const revealScrollAnchorRef = useRef(null);
  const revealAnswerRef = useRef(() => {});
  /** When true, last question was solved via footer Show answer — wait for Continue before firing `onComplete`. */
  const deferLessonCompleteFromRevealRef = useRef(false);
  const [awaitContinueAfterRevealAnswer, setAwaitContinueAfterRevealAnswer] = useState(false);
  const isNarrow = useNarrowViewport(640);

  const q = questions[index];
  const isLast = questions.length > 0 && index >= questions.length - 1;
  const followOpen = phase === 'follow_pick' || phase === 'follow_reveal';
  const isActivityComplete = !!(isLast && phase === 'reveal' && q && q.choices?.find((c) => c.id === picked)?.correct);
  /** Guided lesson shell: select an option, then tap footer Check (no grading until Check). */
  const shellDeferredCheck = typeof onRegisterLessonFooterActions === 'function';

  const tapFirstTitleDistinctFromPrompt = useMemo(() => {
    if (!tapFirst || !q) return true;
    const t = typeof title === 'string' ? title.trim() : '';
    if (!t || t.toLowerCase() === 'quiz') return true;
    const line = mcPromptPrimaryLineForTitleDedupe(q.prompt);
    return normalizePromptForDedupe(t) !== normalizePromptForDedupe(line);
  }, [tapFirst, q, title]);

  const getExplanation = useCallback(() => questions[index]?.explanation, [questions, index]);

  const effectiveHintsForGuided = useMemo(() => {
    const list = [];
    const pushUnique = (s) => {
      const t = typeof s === 'string' ? s.trim() : '';
      if (!t || list.includes(t)) return;
      list.push(t);
    };
    if (Array.isArray(hints)) {
      for (const h of hints) {
        if (typeof h === 'string') pushUnique(h);
        else if (h && typeof h === 'object' && typeof h.text === 'string') pushUnique(h.text);
      }
    }
    if (tapFirst && typeof q?.prompt === 'string') {
      const parsedGp = parseGoalPlanPrompt(q.prompt);
      if (parsedGp?.goal) pushUnique(parsedGp.goal);
    }
    if (tapFirst && followOpen && q?.followUp?.hint) pushUnique(String(q.followUp.hint));
    if (tapFirst && typeof content.instructions === 'string') pushUnique(content.instructions);
    if (tapFirst) {
      for (const k of ['goal', 'objective', 'helperText', 'clue', 'hint', 'instruction']) {
        const v = content[k];
        if (typeof v === 'string') pushUnique(v);
      }
    }
    for (const k of ['quickCheck', 'quick_check', 'tinyClue', 'clue', 'helperText']) {
      const v = content[k];
      if (typeof v === 'string') pushUnique(v);
    }
    return list;
  }, [hints, tapFirst, followOpen, q, content]);

  const effectiveGuidedEnabled =
    guidedHintsEnabled ||
    (tapFirst && followOpen && !!(q?.followUp?.hint && String(q.followUp.hint).trim()));

  const guided = useGuidedHints({
    activityId,
    hints: effectiveHintsForGuided,
    explanation,
    getExplanation,
    isActivityComplete,
    enabled: effectiveGuidedEnabled,
  });
  const { resetSession } = guided;

  useEffect(() => {
    resetSession();
    setShellWrongReveal(null);
    setAwaitContinueAfterRevealAnswer(false);
    deferLessonCompleteFromRevealRef.current = false;
  }, [index, resetSession]);

  useEffect(() => {
    if (!followOpen || isNarrow || tapFirst) return;
    const t = window.setTimeout(() => {
      quickCheckRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 80);
    return () => window.clearTimeout(t);
  }, [followOpen, isNarrow, index, tapFirst]);

  const reportProgress = useCallback(
    (detail) => {
      onProgress?.({
        activityId,
        engine: 'multiple_choice',
        ...detail,
      });
    },
    [activityId, onProgress],
  );

  const clearRevealTimer = useCallback(() => {
    if (revealTimerRef.current != null) {
      clearTimeout(revealTimerRef.current);
      revealTimerRef.current = null;
    }
  }, []);

  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const completionSentRef = useRef(false);

  const flushLessonComplete = useCallback(() => {
    if (completionSentRef.current) return;
    completionSentRef.current = true;
    onCompleteRef.current?.({
      activityId,
      engine: 'multiple_choice',
      totalQuestions: questions.length,
      correctCount,
      helpedCount,
    });
    trackMiniAppEvent.completed(activityId);
  }, [activityId, questions.length, correctCount, helpedCount]);

  /** Lesson shell must hear `onComplete` even if reveal timer is cleared by effect churn (`advanceFromReveal` deps). */
  useEffect(() => {
    if (!isActivityComplete) {
      completionSentRef.current = false;
      return;
    }
    if (awaitContinueAfterRevealAnswer) {
      return;
    }
    if (deferLessonCompleteFromRevealRef.current) {
      deferLessonCompleteFromRevealRef.current = false;
      setAwaitContinueAfterRevealAnswer(true);
      return;
    }
    flushLessonComplete();
  }, [isActivityComplete, awaitContinueAfterRevealAnswer, flushLessonComplete]);

  const advanceFromReveal = useCallback(
    (atIdx) => {
      clearRevealTimer();
      setShellWrongReveal(null);
      const last = questions.length > 0 && atIdx >= questions.length - 1;
      if (last) {
        flushLessonComplete();
        return;
      }
      setIndex(atIdx + 1);
      setPicked(null);
      setFollowPicked(null);
      setPhase('pick');
      setHelpedThisQuestion(false);
      reportProgress({ phase: 'nav', questionIndex: atIdx + 1, total: questions.length });
    },
    [clearRevealTimer, flushLessonComplete, questions.length, reportProgress],
  );

  const selectedForReveal = phase === 'reveal' && q ? q.choices?.find((c) => c.id === picked) : null;
  const revealWasCorrect = !!selectedForReveal?.correct;

  useEffect(() => {
    if (phase !== 'reveal') {
      clearRevealTimer();
      return;
    }
    if (shellDeferredCheck) {
      clearRevealTimer();
      return;
    }
    // Wrong answers should not auto-skip to the next question — confusing for young learners.
    if (!revealWasCorrect) {
      clearRevealTimer();
      return;
    }
    const atIdx = index;
    const onLastQuestion = questions.length > 0 && atIdx >= questions.length - 1;
    clearRevealTimer();
    // Last question: completion is driven by `isActivityComplete` effect (timer was often cleared before firing).
    if (onLastQuestion) return;
    revealTimerRef.current = setTimeout(() => {
      revealTimerRef.current = null;
      advanceFromReveal(atIdx);
    }, ENGINE_AUTO_ADVANCE_MS);
    return () => clearRevealTimer();
  }, [
    phase,
    index,
    advanceFromReveal,
    clearRevealTimer,
    revealWasCorrect,
    questions.length,
    shellDeferredCheck,
  ]);

  /** After feedback appears, scroll the card so learners see the message without hunting. */
  useEffect(() => {
    if (phase !== 'reveal' && !(shellDeferredCheck && shellWrongReveal)) return undefined;
    let cancelled = false;
    const scroll = () => {
      if (cancelled) return;
      const el = revealScrollAnchorRef.current;
      if (!el) return;
      const reduceMotion =
        typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      el.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'end',
        inline: 'nearest',
      });
    };
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(scroll);
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(id);
    };
  }, [phase, index, questions, shellDeferredCheck, shellWrongReveal]);

  const applyMainAnswer = (choiceId) => {
    if (phase !== 'pick' || !q) return;
    const choice = q.choices?.find((c) => c.id === choiceId);
    if (!choice) return;
    guided.noteUserAction();
    setShellWrongReveal(null);
    const ok = !!choice.correct;
    if (ok) {
      setPicked(choiceId);
      setCorrectCount((c) => c + 1);
      if (helpedThisQuestion) setHelpedCount((h) => h + 1);
      setA11yMsg('Correct!');
      celebrate();
      setPhase('reveal');
      guided.noteCorrectAnswer();
      trackMiniAppEvent.interaction(activityId, helpedThisQuestion ? 'correct_after_help' : 'correct');
    } else if (q.followUp) {
      setPicked(choiceId);
      setA11yMsg('Not quite — let us walk through it.');
      setFollowPicked(null);
      setPhase('follow_pick');
      guided.noteIncorrectAnswer();
      trackMiniAppEvent.interaction(activityId, 'followup_open');
    } else if (shellDeferredCheck) {
      setShellWrongReveal({ wrongChoiceId: choiceId });
      setPicked(null);
      setA11yMsg('Incorrect.');
      guided.noteIncorrectAnswer();
      trackMiniAppEvent.interaction(activityId, 'incorrect');
    } else {
      setPicked(choiceId);
      setA11yMsg('Incorrect.');
      setPhase('reveal');
      guided.noteIncorrectAnswer();
      trackMiniAppEvent.interaction(activityId, 'incorrect');
    }
    reportProgress({
      phase: 'answer',
      questionIndex: index,
      total: questions.length,
      lastCorrect: ok,
      branched: !ok && !!q.followUp,
    });
  };

  const pickChoice = (choiceId) => {
    if (phase !== 'pick' || !q) return;
    if (shellDeferredCheck) {
      setShellWrongReveal(null);
      setPicked(choiceId);
      return;
    }
    applyMainAnswer(choiceId);
  };

  const commitMainFromFooter = () => {
    if (!shellDeferredCheck || phase !== 'pick' || picked == null) return;
    applyMainAnswer(picked);
  };

  const applyFollowAnswer = (choiceId) => {
    if (phase !== 'follow_pick' || !q?.followUp) return;
    const choice = q.followUp.choices?.find((c) => c.id === choiceId);
    if (!choice) return;
    guided.noteUserAction();
    setFollowPicked(choiceId);
    setPhase('follow_reveal');
    if (!choice.correct) guided.noteIncorrectAnswer();
    trackMiniAppEvent.interaction(activityId, choice.correct ? 'followup_correct' : 'followup_wrong');
    reportProgress({
      phase: 'followup_answer',
      questionIndex: index,
      total: questions.length,
      followCorrect: !!choice.correct,
    });
  };

  const pickFollowUp = (choiceId) => {
    if (phase !== 'follow_pick' || !q?.followUp) return;
    if (shellDeferredCheck) {
      setFollowPicked(choiceId);
      return;
    }
    applyFollowAnswer(choiceId);
  };

  const commitFollowFromFooter = () => {
    if (!shellDeferredCheck || phase !== 'follow_pick' || followPicked == null) return;
    applyFollowAnswer(followPicked);
  };

  const retryMain = () => {
    clearRevealTimer();
    guided.resetSession();
    setHelpedThisQuestion(true);
    setPicked(null);
    setFollowPicked(null);
    setShellWrongReveal(null);
    setPhase('pick');
    setA11yMsg('Try the question again.');
    trackMiniAppEvent.interaction(activityId, 'main_retry');
  };

  /** After a wrong pick with no follow-up: stay on this question until the learner chooses. */
  const tryAgainAfterWrong = () => {
    clearRevealTimer();
    guided.noteUserAction();
    setPicked(null);
    setShellWrongReveal(null);
    setPhase('pick');
    setA11yMsg('Try again.');
    trackMiniAppEvent.interaction(activityId, 'retry_after_wrong');
  };

  revealAnswerRef.current = () => {
    if (isActivityComplete) return;
    clearRevealTimer();
    guided.noteUserAction();
    const qq = questions[index];
    if (!qq) return;
    if (phase === 'follow_pick') {
      const fc = qq.followUp?.choices?.find((c) => c.correct);
      if (fc) applyFollowAnswer(fc.id);
      return;
    }
    if (phase === 'reveal') {
      const sel = qq.choices?.find((c) => c.id === picked);
      if (sel?.correct) return;
      tryAgainAfterWrong();
      window.setTimeout(() => {
        const qn = questions[index];
        const cor = qn?.choices?.find((c) => c.correct);
        if (cor) {
          setHelpedThisQuestion(true);
          const lastQ = questions.length > 0 && index >= questions.length - 1;
          if (shellDeferredCheck && lastQ) deferLessonCompleteFromRevealRef.current = true;
          applyMainAnswer(cor.id);
        }
      }, 0);
      return;
    }
    if (phase === 'pick') {
      const cor = qq.choices?.find((c) => c.correct);
      if (cor) {
        setHelpedThisQuestion(true);
        if (shellDeferredCheck && isLast) deferLessonCompleteFromRevealRef.current = true;
        applyMainAnswer(cor.id);
      }
    }
  };

  useEffect(() => {
    if (!onRegisterLessonFooterActions) return undefined;
    if (questions.length === 0) {
      onRegisterLessonFooterActions(null);
      return undefined;
    }
    if (shellDeferredCheck && isActivityComplete && awaitContinueAfterRevealAnswer) {
      onRegisterLessonFooterActions({
        continueLesson: () => {
          guided.noteUserAction();
          setAwaitContinueAfterRevealAnswer(false);
          flushLessonComplete();
        },
      });
      return () => onRegisterLessonFooterActions(null);
    }
    if (isActivityComplete) {
      onRegisterLessonFooterActions(null);
      return undefined;
    }
    const showAnswer = () => revealAnswerRef.current();

    if (shellDeferredCheck) {
      if (phase === 'pick') {
        onRegisterLessonFooterActions({
          checkAnswer: commitMainFromFooter,
          checkAnswerDisabled: picked == null,
          showAnswer,
        });
        return () => onRegisterLessonFooterActions(null);
      }
      if (phase === 'follow_pick') {
        onRegisterLessonFooterActions({
          checkAnswer: commitFollowFromFooter,
          checkAnswerDisabled: followPicked == null,
          showAnswer,
        });
        return () => onRegisterLessonFooterActions(null);
      }
      if (phase === 'follow_reveal') {
        onRegisterLessonFooterActions({
          continueLesson: () => {
            guided.noteUserAction();
            retryMain();
          },
          showAnswer,
        });
        return () => onRegisterLessonFooterActions(null);
      }
      if (phase === 'reveal' && revealWasCorrect && !isLast) {
        onRegisterLessonFooterActions({
          continueLesson: () => {
            guided.noteUserAction();
            advanceFromReveal(index);
          },
          showAnswer,
        });
        return () => onRegisterLessonFooterActions(null);
      }
      onRegisterLessonFooterActions({ showAnswer });
      return () => onRegisterLessonFooterActions(null);
    }

    onRegisterLessonFooterActions({
      showAnswer,
    });
    return () => onRegisterLessonFooterActions(null);
  }, [
    onRegisterLessonFooterActions,
    isActivityComplete,
    questions.length,
    shellDeferredCheck,
    phase,
    picked,
    followPicked,
    revealWasCorrect,
    isLast,
    index,
    advanceFromReveal,
    retryMain,
    shellWrongReveal,
    awaitContinueAfterRevealAnswer,
    flushLessonComplete,
    guided,
  ]);

  if (questions.length === 0 || !q) {
    return (
      <div style={getEngineRootStyle({ singleSurface })}>
        <p style={instructionsStyle}>No questions in this activity yet.</p>
      </div>
    );
  }

  const selected = q.choices?.find((c) => c.id === picked);
  const wasCorrect = selected?.correct;
  const followPickedMeta =
    q?.followUp?.choices && followPicked ? q.followUp.choices.find((x) => x.id === followPicked) : null;
  const followRevealWasCorrect = phase === 'follow_reveal' ? !!followPickedMeta?.correct : false;

  const showFollowAsMain = !!(tapFirst && followOpen && q.followUp);
  const showNestedQuickCheck = !tapFirst && followOpen && q.followUp;
  const qcMinimal = showNestedQuickCheck && singleSurface;

  const choiceN = q?.choices?.length || 0;
  const choiceGap =
    tapFirst && isNarrow ? (choiceN > 3 ? 6 : 8) : tapFirst ? (choiceN > 3 ? 8 : 10) : 10;

  const mainChoicesBlock = (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: choiceGap,
          ...(isNarrow && followOpen && !tapFirst
            ? { opacity: 0.72, marginTop: 4 }
            : {}),
        }}
      >
        {q.choices?.map((c, ci) => {
          const shellRevealWrongOnly = !!(shellDeferredCheck && shellWrongReveal);
          const revealCorrect = phase === 'reveal' && wasCorrect;
          const revealWrong = phase === 'reveal' && !wasCorrect;
          let bg = 'var(--color-bg)';
          if (revealCorrect) {
            if (c.correct) bg = 'rgba(34, 197, 94, 0.2)';
            else if (picked != null && c.id === picked && !c.correct) bg = 'rgba(239, 68, 68, 0.15)';
          } else if (revealWrong) {
            if (picked != null && c.id === picked && !c.correct) bg = 'rgba(239, 68, 68, 0.15)';
          } else if (shellRevealWrongOnly) {
            if (shellWrongReveal?.wrongChoiceId === c.id && !c.correct) {
              bg = 'rgba(239, 68, 68, 0.15)';
            }
          } else if (shellDeferredCheck && phase === 'pick' && picked === c.id) {
            bg = 'rgba(139, 92, 246, 0.14)';
          }
          const dimNonSelectedWrong =
            phase === 'reveal' && picked != null && !c.correct && c.id !== picked;
          return (
            <button
              key={c.id}
              type="button"
              disabled={phase !== 'pick'}
              onClick={() => pickChoice(c.id)}
              aria-pressed={picked === c.id}
              style={{
                ...secondaryBtn,
                width: '100%',
                textAlign: 'left',
                justifyContent: 'flex-start',
                background: bg,
                opacity: dimNonSelectedWrong ? 0.65 : 1,
                minHeight: tapFirst ? 56 : 44,
                padding: tapFirst ? '14px 16px' : '10px 14px',
                fontSize: tapFirst ? 16 : 14,
              }}
            >
              <span style={{ marginRight: 8 }} aria-hidden>
                {String.fromCharCode(65 + ci)}.
              </span>
              {c.text}
            </button>
          );
        })}
      </div>
      {shellDeferredCheck && shellWrongReveal ? (
        <div
          ref={revealScrollAnchorRef}
          role="status"
          style={{
            marginTop: 10,
            padding: '10px 12px',
            borderRadius: 14,
            lineHeight: 1.4,
            ...feedbackIncorrect,
          }}
        >
          <span style={{ fontWeight: 800 }}>Not quite.</span>
          <span style={{ display: 'block', marginTop: 6, fontWeight: 600, fontSize: 14 }}>
            That is not the best fit. Pick another answer, then tap Check.
          </span>
        </div>
      ) : null}
      {isNarrow && followOpen && !tapFirst ? (
        <p
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: 'var(--color-accent)',
            margin: '10px 0 0',
            lineHeight: 1.35,
          }}
        >
          ↑ Answer the quick check above — then you can try these again.
        </p>
      ) : null}
      {helpedThisQuestion && phase === 'pick' ? (
        <p
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--color-text)',
            opacity: 0.7,
            marginTop: 10,
          }}
        >
          Try the question again with what you just learned.
        </p>
      ) : null}
    </>
  );

  const followChoicesFlatBlock =
    q.followUp && showFollowAsMain ? (
      <>
        {singleSurface && q.followUp.hint ? (
          <p
            style={{
              margin: '0 0 10px',
              fontSize: 14,
              fontWeight: 600,
              lineHeight: 1.4,
              color: 'var(--color-text)',
              opacity: 0.82,
              padding: '10px 12px',
              borderRadius: 12,
              background: 'rgba(245, 158, 11, 0.08)',
            }}
          >
            {q.followUp.hint}
          </p>
        ) : null}
        <p
          id={`mc-fu-${q.id}`}
          style={{
            margin: '0 0 14px',
            fontSize: 17,
            fontWeight: 800,
            lineHeight: 1.35,
            color: 'var(--color-text)',
          }}
        >
          {q.followUp.prompt}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {q.followUp.choices?.map((c, ci) => {
            const revealed = phase === 'follow_reveal';
            let bg = 'var(--color-bg-card)';
            if (revealed) {
              if (followRevealWasCorrect) {
                if (c.correct) bg = 'rgba(34, 197, 94, 0.22)';
                else if (c.id === followPicked && !c.correct) bg = 'rgba(245, 158, 11, 0.14)';
              } else if (followPicked && c.id === followPicked && !c.correct) {
                bg = 'rgba(245, 158, 11, 0.14)';
              }
            } else if (shellDeferredCheck && phase === 'follow_pick' && followPicked === c.id) {
              bg = 'rgba(139, 92, 246, 0.14)';
            }
            const borderGreen = revealed && followRevealWasCorrect && c.correct;
            const borderWrongPick = revealed && !followRevealWasCorrect && c.id === followPicked && !c.correct;
            return (
              <button
                key={c.id}
                type="button"
                disabled={phase !== 'follow_pick'}
                onClick={() => pickFollowUp(c.id)}
                aria-pressed={followPicked === c.id}
                style={{
                  ...secondaryBtn,
                  minHeight: tapFirst ? 56 : 48,
                  padding: tapFirst ? '14px 16px' : '10px 14px',
                  fontSize: tapFirst ? 16 : 14,
                  width: '100%',
                  textAlign: 'left',
                  justifyContent: 'flex-start',
                  background: bg,
                  borderColor: borderGreen
                    ? 'rgba(34, 197, 94, 0.55)'
                    : borderWrongPick
                      ? 'rgba(245, 158, 11, 0.55)'
                      : 'var(--color-border)',
                }}
              >
                <span style={{ marginRight: 8 }} aria-hidden>
                  {String.fromCharCode(65 + ci)}.
                </span>
                {c.text}
              </button>
            );
          })}
        </div>
        {phase === 'follow_reveal' ? (
          <div style={{ marginTop: 14 }}>
            {(() => {
              const fc = q.followUp.choices?.find((c) => c.id === followPicked);
              const txt = fc?.correct
                ? fc?.explanation || 'Nice — you spotted the key clue.'
                : shellDeferredCheck
                  ? 'Not quite. Tap Continue below to try the big question again.'
                  : 'Not quite. Tap Try the main question again below.';
              return (
                <div
                  role="status"
                  style={fc?.correct ? feedbackCorrect : { ...feedbackWarmHint, fontSize: 15 }}
                >
                  {txt}
                </div>
              );
            })()}
            {!shellDeferredCheck ? (
              <button type="button" style={{ ...primaryBtn, marginTop: 14 }} onClick={retryMain}>
                Try the main question again
              </button>
            ) : null}
          </div>
        ) : null}
      </>
    ) : null;

  const quickCheckBlock =
    showNestedQuickCheck ? (
      <div
        ref={quickCheckRef}
        role="region"
        aria-labelledby={qcMinimal ? `mc-fu-prompt-${q.id}` : `mc-quick-check-${q.id}`}
        style={
          qcMinimal
            ? {
                marginTop: isNarrow ? 4 : 12,
                marginBottom: 16,
                padding: '12px 0 14px',
                borderRadius: 0,
                border: 'none',
                borderTop: '1px solid rgba(148, 163, 184, 0.35)',
                background: 'transparent',
                boxShadow: 'none',
                scrollMarginTop: 12,
              }
            : {
                marginTop: isNarrow ? 6 : 20,
                marginBottom: 24,
                padding: '16px 16px 18px',
                borderRadius: 18,
                border: '1px solid var(--color-border)',
                borderLeft: '4px solid var(--color-accent)',
                background:
                  'linear-gradient(165deg, rgba(251, 191, 36, 0.09) 0%, var(--color-bg) 52%, var(--color-bg-card) 100%)',
                boxShadow: '0 4px 20px var(--color-shadow)',
                scrollMarginTop: 16,
              }
        }
      >
        {qcMinimal ? null : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 12,
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 12,
                background: 'rgba(251, 191, 36, 0.18)',
                color: 'var(--color-accent)',
                flexShrink: 0,
              }}
              aria-hidden
            >
              <Lightbulb size={22} strokeWidth={2.25} aria-hidden />
            </span>
            <div>
              <div
                id={`mc-quick-check-${q.id}`}
                style={{
                  fontSize: 15,
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  color: 'var(--color-text)',
                  lineHeight: 1.2,
                }}
              >
                Quick check
              </div>
              <p
                style={{
                  margin: '4px 0 0',
                  fontSize: 13,
                  fontWeight: 600,
                  lineHeight: 1.35,
                  color: 'var(--color-text)',
                  opacity: 0.72,
                }}
              >
                {isNarrow
                  ? 'Use the hint, then tap an answer in this section (your main choices stay below).'
                  : 'A tiny clue — then pick the best answer below.'}
              </p>
            </div>
          </div>
        )}

        {q.followUp.hint ? (
          <div
            style={{
              ...feedbackWarmHint,
              marginBottom: 14,
              padding: qcMinimal ? '10px 12px' : '12px 14px',
              borderRadius: qcMinimal ? 12 : 14,
              borderWidth: 1,
              borderStyle: 'solid',
              ...(qcMinimal ? { borderColor: 'rgba(217, 119, 6, 0.25)', background: 'rgba(245, 158, 11, 0.08)' } : {}),
            }}
          >
            {!qcMinimal ? (
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'var(--color-accent)',
                  marginBottom: 6,
                }}
              >
                Hint
              </div>
            ) : null}
            <p style={{ margin: 0, fontSize: 15, lineHeight: 1.45, fontWeight: 600 }}>
              {q.followUp.hint}
            </p>
          </div>
        ) : null}

        <p
          id={`mc-fu-prompt-${q.id}`}
          style={{
            margin: '0 0 14px',
            fontSize: 16,
            fontWeight: 800,
            lineHeight: 1.35,
            color: 'var(--color-text)',
          }}
        >
          {q.followUp.prompt}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {q.followUp.choices?.map((c, ci) => {
            const revealed = phase === 'follow_reveal';
            let bg = 'var(--color-bg-card)';
            if (revealed) {
              if (followRevealWasCorrect) {
                if (c.correct) bg = 'rgba(34, 197, 94, 0.22)';
                else if (c.id === followPicked && !c.correct) bg = 'rgba(245, 158, 11, 0.14)';
              } else if (followPicked && c.id === followPicked && !c.correct) {
                bg = 'rgba(245, 158, 11, 0.14)';
              }
            } else if (shellDeferredCheck && phase === 'follow_pick' && followPicked === c.id) {
              bg = 'rgba(139, 92, 246, 0.14)';
            }
            const borderGreen = revealed && followRevealWasCorrect && c.correct;
            const borderWrongPick = revealed && !followRevealWasCorrect && c.id === followPicked && !c.correct;
            return (
              <button
                key={c.id}
                type="button"
                disabled={phase !== 'follow_pick'}
                onClick={() => pickFollowUp(c.id)}
                aria-pressed={followPicked === c.id}
                style={{
                  ...secondaryBtn,
                  minHeight: tapFirst ? 56 : 48,
                  padding: tapFirst ? '14px 16px' : '10px 14px',
                  fontSize: tapFirst ? 16 : 14,
                  width: '100%',
                  textAlign: 'left',
                  justifyContent: 'flex-start',
                  background: bg,
                  borderColor: borderGreen
                    ? 'rgba(34, 197, 94, 0.55)'
                    : borderWrongPick
                      ? 'rgba(245, 158, 11, 0.55)'
                      : 'var(--color-border)',
                }}
              >
                <span style={{ marginRight: 8 }} aria-hidden>
                  {String.fromCharCode(65 + ci)}.
                </span>
                {c.text}
              </button>
            );
          })}
        </div>

        {phase === 'follow_reveal' ? (
          <div style={{ marginTop: 14 }}>
            {(() => {
              const fc = q.followUp.choices?.find((c) => c.id === followPicked);
              const txt = fc?.correct
                ? fc?.explanation || 'Nice — you spotted the key clue.'
                : shellDeferredCheck
                  ? 'Not quite. Tap Continue below to try the big question again.'
                  : 'Not quite. Tap Try the main question again below.';
              return (
                <div
                  role="status"
                  style={fc?.correct ? feedbackCorrect : { ...feedbackWarmHint, fontSize: 15 }}
                >
                  {txt}
                </div>
              );
            })()}
            {!shellDeferredCheck ? (
              <button type="button" style={{ ...primaryBtn, marginTop: 14 }} onClick={retryMain}>
                Try the main question again
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    ) : null;

  const mainTapRevealBlock =
    phase === 'reveal' && !showFollowAsMain && (wasCorrect || !shellDeferredCheck) ? (
      <div
        ref={revealScrollAnchorRef}
        style={{ scrollMarginTop: 12, scrollMarginBottom: 24 }}
      >
        <div
          role="status"
          style={{
            marginTop: 10,
            padding: '10px 12px',
            borderRadius: 14,
            lineHeight: 1.4,
            ...(wasCorrect ? feedbackCorrect : feedbackIncorrect),
          }}
        >
          {wasCorrect ? (
            <>
              <span style={{ fontWeight: 800 }}>
                {helpedThisQuestion ? 'Nice — you got it with a hint.' : 'Great!'}
              </span>
              {q.explanation ? (
                <span style={{ display: 'block', marginTop: 6, fontWeight: 600, fontSize: 14 }}>
                  {q.explanation}
                </span>
              ) : null}
            </>
          ) : (
            <>
              <span style={{ fontWeight: 800 }}>Not quite.</span>
              <span style={{ display: 'block', marginTop: 6, fontWeight: 600, fontSize: 14 }}>
                Think about it, then try another answer — or use the buttons below.
              </span>
            </>
          )}
        </div>
        {wasCorrect ? (
          <div style={{ marginTop: 12 }}>
            <p style={{ ...instructionsStyle, fontSize: 13, marginBottom: 8, opacity: 0.75 }}>
              {shellDeferredCheck
                ? isLast
                  ? 'Tap Continue below when you are ready.'
                  : 'Tap Continue below for the next question.'
                : isLast
                  ? 'Use Continue below to go on when you are ready.'
                  : (
                      <>
                        Next question in a moment… <span style={{ fontWeight: 700 }}>or continue now</span>.
                      </>
                    )}
            </p>
            {!shellDeferredCheck ? (
              <button
                type="button"
                style={primaryBtn}
                onClick={() => {
                  guided.noteUserAction();
                  advanceFromReveal(index);
                }}
              >
                {isLast ? 'Finish now' : 'Next question →'}
              </button>
            ) : null}
          </div>
        ) : !shellDeferredCheck ? (
          <div style={{ marginTop: 12 }}>
            <p style={{ ...instructionsStyle, fontSize: 13, marginBottom: 10, opacity: 0.85 }}>
              Pick another answer, or move on when you are ready.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button type="button" style={primaryBtn} onClick={tryAgainAfterWrong}>
                Try again
              </button>
              <button
                type="button"
                style={{ ...secondaryBtn, width: '100%', justifyContent: 'center' }}
                onClick={() => {
                  guided.noteUserAction();
                  advanceFromReveal(index);
                }}
              >
                {isLast ? 'Finish activity' : 'Next question'}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    ) : null;

  const shellMerged = {
    ...getEngineRootStyle({ singleSurface }),
    ...(tapFirst
      ? isNarrow
        ? {
            padding: choiceN > 3 ? '10px 8px 12px' : '12px 10px 14px',
            ...(singleSurface ? {} : { borderRadius: 18 }),
          }
        : { padding: '14px 12px 18px' }
      : {}),
    position: 'relative',
    ...(guided.contentReserveStyle || {}),
  };

  return (
    <div style={shellMerged}>
      <CelebrationOverlay visible={showCelebration} message="Nice thinking!" />
      <A11yAnnouncement message={a11yMsg} />
      <GuidedHintOverlay
        open={guided.open}
        isNarrow={guided.isNarrow}
        visibleLevel={guided.visibleLevel}
        maxLevel={guided.maxLevel}
        isInteractionPhase={guided.isInteractionPhase}
        anotherClueEnabled={guided.anotherClueEnabled}
        hintEntry={guided.hintEntry}
        showHowAvailable={guided.showHowAvailable}
        resolvedExplanation={guided.resolvedExplanation}
        explanationOpen={guided.explanationOpen}
        onDismiss={guided.dismissOverlay}
        onAnotherClue={guided.anotherClue}
        onReadAloud={() =>
          guidedHintReadAloud(guided.hintEntry, activityId, guided.readAloudTrackLevel)
        }
        onShowHow={guided.openExplanation}
      />
      <GuidedHintHelpButton visible={guided.showHelpButton} onClick={guided.requestHintManually} />

      {tapFirst &&
      typeof title === 'string' &&
      title.trim() &&
      title.trim().toLowerCase() !== 'quiz' &&
      !suppressActivityTitle &&
      tapFirstTitleDistinctFromPrompt ? (
        <p
          style={{
            margin: '0 0 6px',
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'var(--color-text-soft, var(--color-text))',
            opacity: 0.72,
          }}
        >
          {title}
        </p>
      ) : null}
      {!tapFirst && !suppressActivityTitle ? <h2 style={titleStyle}>{title}</h2> : null}
      {!tapFirst && content.instructions && <p style={instructionsStyle}>{content.instructions}</p>}

      <div
        role="group"
        aria-labelledby={showFollowAsMain ? `mc-fu-${q.id}` : `mc-q-${q.id}`}
        style={{ marginBottom: tapFirst ? 10 : 16 }}
      >
        {showFollowAsMain ? (
          followChoicesFlatBlock
        ) : (
          <>
            {!tapFirst ? (
              <div
                id={`mc-q-${q.id}`}
                style={{
                  fontSize: 17,
                  fontWeight: 700,
                  color: 'var(--color-text)',
                  marginBottom: 12,
                  lineHeight: 1.35,
                }}
              >
                Question {index + 1} of {questions.length}
              </div>
            ) : (
              <p
                id={`mc-q-${q.id}`}
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: 'var(--color-text-soft, var(--color-text))',
                  margin: '0 0 8px',
                  lineHeight: 1.35,
                  opacity: 0.75,
                }}
              >
                Question {index + 1} of {questions.length}
              </p>
            )}
            {tapFirst ? (
              <FlatMcPrompt prompt={q.prompt} narrow={isNarrow} />
            ) : (
              <GoalPlanPromptBlocks prompt={q.prompt} />
            )}
            {isNarrow && followOpen && !tapFirst ? (
              <>
                {quickCheckBlock}
                {mainChoicesBlock}
              </>
            ) : (
              <>
                {mainChoicesBlock}
                {quickCheckBlock}
              </>
            )}
            {mainTapRevealBlock}
          </>
        )}
      </div>

    </div>
  );
}
